/**
 * TeamStrength Hub - Organization Switcher
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Users, Building2, ArrowRight, Shield } from 'lucide-react';

const OrgSwitcher = () => {
  const { organizations, switchOrg, isSuperAdmin } = useAuth();
  const navigate = useNavigate();

  const handleSelectOrg = (org) => {
    switchOrg(org);
    navigate('/dashboard/studio');
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8 animate-fade-in">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-brand-gradient mb-4">
            <Users className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Manrope' }}>
            Select Organization
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Choose which organization you want to work with
          </p>
        </div>

        {/* Organizations list */}
        <div className="space-y-3 animate-fade-in stagger-1">
          {organizations.length === 0 ? (
            <Card className="text-center py-12">
              <CardContent>
                <Building2 className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                <p className="text-slate-500 dark:text-slate-400">
                  You are not a member of any organization yet.
                </p>
                {isSuperAdmin && (
                  <Button 
                    className="mt-4 bg-violet-600 hover:bg-violet-700"
                    onClick={() => navigate('/dashboard/admin')}
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    Go to Admin Panel
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            organizations.map((org) => (
              <Card 
                key={org.org_id}
                className="cursor-pointer hover:shadow-md transition-shadow hover:border-violet-200 dark:hover:border-violet-900"
                onClick={() => handleSelectOrg(org)}
                data-testid={`org-card-${org.org_id}`}
              >
                <CardContent className="flex items-center justify-between p-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-violet-600 dark:text-violet-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 dark:text-white" style={{ fontFamily: 'Manrope' }}>
                        {org.name}
                      </h3>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        Role: <span className="capitalize">{org.role.replace('_', ' ')}</span>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`status-badge ${org.status === 'active' ? 'status-active' : 'status-draft'}`}>
                      {org.status}
                    </span>
                    <ArrowRight className="w-5 h-5 text-slate-400" />
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* SuperAdmin shortcut */}
        {isSuperAdmin && organizations.length > 0 && (
          <div className="mt-6 text-center animate-fade-in stagger-2">
            <Button 
              variant="outline"
              onClick={() => navigate('/dashboard/admin')}
              data-testid="admin-panel-btn"
            >
              <Shield className="w-4 h-4 mr-2" />
              SuperAdmin Panel
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrgSwitcher;
